import React from "react";
import { Play, MoreHorizontal, Clock, Archive, Zap } from "lucide-react";
import Button from "../../components/ui/Button";
import DropdownMenu from "../../components/ui/DropdownMenu";

/**
 * Selection toolbar rendered above the scenario table when at least one
 * scenario is checked. Extracted from Scenario.js so the exactly-one
 * "Run Stress Test" gate can be tested in isolation without mounting the
 * full (53KB) scenario list page.
 *
 * @param {Array} selectedIds - ids of the currently-selected scenarios
 * @param {boolean} canWrite - scenario write permission (useCan("scenario", "write"))
 * @param {boolean} canStressTest - workspace has live_simulations.stress_testing
 * @param {function} onRunSelected - runs all selected scenarios
 * @param {function} onScheduleSelected - schedules all selected scenarios
 * @param {function} onArchiveSelected - archives all selected scenarios
 * @param {function} onRunStressTest - (scenarioId) => void, opens the stress-test
 *   wizard for the single selected scenario. Only wired/rendered when exactly
 *   one scenario is selected.
 * @param {boolean} loading
 */
export function ScenarioSelectionToolbar({
  selectedIds = [],
  canWrite,
  canStressTest = false,
  onRunSelected,
  onScheduleSelected,
  onArchiveSelected,
  onRunStressTest,
  loading = false,
}) {
  if (selectedIds.length === 0 || !canWrite) return null;

  return (
    <div className="flex gap-2">
      <Button variant="primary" onClick={onRunSelected} disabled={loading}>
        <Play className="h-5 w-5" />
        <span>Run Selected ({selectedIds.length})</span>
      </Button>
      {canStressTest && selectedIds.length === 1 && (
        <Button
          variant="secondary"
          onClick={() => onRunStressTest(selectedIds[0])}
          disabled={loading}
        >
          <Zap className="h-5 w-5" />
          <span>Run Stress Test</span>
        </Button>
      )}
      <DropdownMenu
        trigger={
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <MoreHorizontal className="h-5 w-5" />
          </div>
        }
        triggerClassName="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
        items={[
          {
            label: "Schedule Selected",
            icon: <Clock className="h-4 w-4" />,
            onClick: onScheduleSelected,
          },
          {
            label: `Archive Selected (${selectedIds.length})`,
            icon: <Archive className="h-4 w-4" />,
            onClick: onArchiveSelected,
          },
        ]}
      />
    </div>
  );
}

export default ScenarioSelectionToolbar;
